import ItemsSelectedListModal from '@/components/Movements/Quotations/QuotationsRequestPurchase/ItemsSelectedListModal';
import SendRequestPurchaseModal, {
  SendRequestPurchaseModalFormValues,
} from '@/components/Movements/Quotations/QuotationsRequestPurchase/SendRequestPurchaseModal';
import { InfiniteTable } from '@/components/_common';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { QuotationStatus } from '@/core/domain/stock/Quotations/Quotations.types';
import {
  useQuotationOffers,
  useQuotationOffersWinners,
} from '@/core/domain/stock/QuotationsOffers/QuotationsOffers.hooks';
import {
  QuotationOffersResponse,
  QuotationOffersWinnersRequest,
  QuotationSuppliersOffers,
} from '@/core/domain/stock/QuotationsOffers/QuotationsOffers.types';
import { useDisclosure, useWindowSize } from '@/hooks';
import { convertFileToBase64 } from '@/utils/convertFileToBase64';
import { Button, Col, Divider, Form, Radio, Row, Tooltip } from 'antd';
import Table, { ColumnsType } from 'antd/lib/table';
import { RcFile } from 'antd/lib/upload';

import React, { useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useQuotationContext } from '..';
import { getTableScrollX } from '@/utils/table';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import formatterPrice from '@/utils/number/formatterPrice';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';
import formatterPercent from '@/utils/number/formatterPercent';
import formatterNumber from '@/utils/number/formatterNumber';

enum AutomaticSelectionRadio {
  lowestPriceAndLowestTerm = 'LOWEST_PRICE_AND_LOWEST_TERM',
  shorterTermAndLowerPrice = 'SHORTER_TERM_AND_LOWER_PRICE',
}

const QuotationPurchaseRequestPage: React.FC = () => {
  const [form] = Form.useForm<SendRequestPurchaseModalFormValues>();
  const { quotationId } = useRouteMatch<{ quotationId: string }>().params;
  const quotation = useQuotationContext();
  const { isMobile } = useWindowSize();

  const [selectedSuppliersWinners, setSelectedSuppliersWinners] = useState<
    QuotationSuppliersOffers[]
  >([]);
  const [fileList, setFileList] = useState<RcFile[]>([]);
  const [selectionAutomaticSelectionType, setSelectionAutomaticSelectionType] =
    useState<AutomaticSelectionRadio>(
      AutomaticSelectionRadio.lowestPriceAndLowestTerm,
    );

  const [modalViewItemsSelected, modalViewItemsSelectedAction] =
    useDisclosure();
  const [modalSendRequestPurchase, modalSendRequestPurchaseAction] =
    useDisclosure();

  const { data, isFetching, fetchNextPage, hasNextPage } = useQuotationOffers(
    Number(quotationId),
    {
      order: 'DESC',
    },
  );

  const quotationsOffers = flatInfiniteResult(data);
  const quotationSendMutation = useQuotationOffersWinners();

  const columns: ColumnsType<QuotationOffersResponse> = [
    {
      key: 'workOrderNumber',
      dataIndex: 'workOrderNumber',
      title: 'Orçamento',
      width: 100,
    },
    {
      key: 'manufacturingCode',
      dataIndex: 'manufacturingCode',
      title: 'Código',
      width: 100,
    },
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Descrição',
      width: 300,
    },
    {
      key: 'quantity',
      dataIndex: 'quantity',
      title: 'Qtde. Solicitada',
      render: (value) => formatterPrice(value),
      width: 150,
    },
    {
      key: 'selectWinners',
      title: 'Ação',
      width: 250,
      align: 'right',
      fixed: 'right',
    },
  ];

  const handleCallback = async () => {
    if (fileList.length) {
      const arrayPromisses = await fileList.map(
        async (file) => await convertFileToBase64(file),
      );

      await Promise.all(arrayPromisses).then((valuesPromisse) => {
        form.setFieldsValue({
          attachments: valuesPromisse.map((base64, index) => ({
            content: base64,
            fileName: fileList[index].name,
            contentType: fileList[index].name.split('.')[1].toUpperCase(),
          })),
        });
      });

      handleSubmit();
    } else {
      form.setFieldsValue({ attachments: [] });

      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();

    const obj: QuotationOffersWinnersRequest = {
      quotationId: Number(quotationId),
      ...values,
      suppliers: values.suppliers.map((offer) => ({
        email: offer.supplier.email,
        id: offer.supplier.id,
      })),
      offers: selectedSuppliersWinners.map((offer) => ({
        itemId: offer.id,
        supplierId: offer.supplier.id,
      })),
    };

    await quotationSendMutation.mutateAsync(obj);

    modalSendRequestPurchaseAction.close();
  };

  const handleValueTotal = (offer: QuotationSuppliersOffers) => {
    return (
      (((offer.unitCost ?? 0) * (offer.quantity ?? 0)) / 100) *
      (100 - (offer.discount ?? 0))
    );
  };

  const handleAutomaticSelectOffer = () => {
    const items = quotationsOffers.map((quotationOffer) =>
      quotationOffer.offers.reduce((acc, curr) => {
        const currTotal = handleValueTotal(curr);
        const accTotal = handleValueTotal(acc);

        if (
          selectionAutomaticSelectionType ===
          AutomaticSelectionRadio.lowestPriceAndLowestTerm
        ) {
          if (currTotal < accTotal) {
            return curr;
          } else if (
            currTotal === accTotal &&
            curr.deliverDays < acc.deliverDays
          ) {
            return curr;
          }
        } else {
          if (curr.deliverDays < acc.deliverDays) {
            return curr;
          } else if (
            curr.deliverDays === acc.deliverDays &&
            currTotal < accTotal
          ) {
            return curr;
          }
        }
        return acc;
      }),
    );

    setSelectedSuppliersWinners(items);
  };

  return (
    <>
      <div>
        <Row gutter={[12, 12]}>
          <Col flex="1">
            <h2>Solicitação de Compra</h2>
          </Col>

          <Col>
            <SpaceForButton>
              <Tooltip
                title={
                  quotation?.status !== QuotationStatus.WinnersSelected &&
                  quotation?.status !== QuotationStatus.Done
                    ? !selectedSuppliersWinners.length
                      ? 'Selecione ao menos 1 vencedor'
                      : undefined
                    : undefined
                }
              >
                <Button
                  block
                  type="primary"
                  onClick={modalViewItemsSelectedAction.open}
                  disabled={
                    quotation?.status !== QuotationStatus.WinnersSelected &&
                    quotation?.status !== QuotationStatus.Done
                      ? !selectedSuppliersWinners.length
                      : quotation?.status !== QuotationStatus.WinnersSelected
                  }
                >
                  Visualizar itens selecionados
                </Button>
              </Tooltip>

              <Tooltip
                title={
                  quotation?.status !== QuotationStatus.WinnersSelected &&
                  quotation?.status !== QuotationStatus.Done
                    ? !selectedSuppliersWinners.length
                      ? 'Selecione ao menos 1 vencedor'
                      : undefined
                    : undefined
                }
              >
                <Button
                  block
                  type="primary"
                  onClick={modalSendRequestPurchaseAction.open}
                  disabled={
                    !selectedSuppliersWinners.length ||
                    quotation?.status === QuotationStatus.WinnersSelected ||
                    quotation?.status === QuotationStatus.Done ||
                    !hasPermissions([
                      Authorities.MOVIMENTATION_QUOTATION_REQUEST_PURCHASE,
                    ])
                  }
                >
                  Enviar pedido de compra
                </Button>
              </Tooltip>
            </SpaceForButton>
          </Col>
        </Row>

        <Divider />

        <Row gutter={[24, 24]}>
          <Col flex="1">
            <Radio.Group
              disabled={
                quotation?.status === QuotationStatus.WinnersSelected ||
                quotation?.status === QuotationStatus.Done
              }
              optionType="button"
              buttonStyle="solid"
              size="large"
              onChange={({ target }) =>
                setSelectionAutomaticSelectionType(target.value)
              }
              defaultValue={selectionAutomaticSelectionType}
              style={{ marginBottom: 20 }}
            >
              <Radio
                style={{ fontSize: isMobile ? '15px' : '' }}
                value={AutomaticSelectionRadio.lowestPriceAndLowestTerm}
              >
                Menor preço x Menor prazo
              </Radio>
              <Radio
                style={{ fontSize: isMobile ? '15px' : '' }}
                value={AutomaticSelectionRadio.shorterTermAndLowerPrice}
              >
                Menor prazo x Menor preço
              </Radio>
            </Radio.Group>
          </Col>

          <Col>
            <Button
              block
              disabled={
                quotation?.status === QuotationStatus.WinnersSelected ||
                quotation?.status === QuotationStatus.Done
              }
              type="primary"
              size="large"
              onClick={handleAutomaticSelectOffer}
            >
              Seleção automática
            </Button>
          </Col>

          <Col span={24}>
            <InfiniteTable
              expandable={{
                rowExpandable: (record) => !!record.offers?.length,
                defaultExpandAllRows: true,
                expandedRowRender: (record) => (
                  <Table
                    dataSource={record.offers}
                    rowKey={(offer) => offer.supplier.id}
                    columns={[
                      {
                        key: 'supplier',
                        dataIndex: ['supplier', 'name'],
                        title: 'Fornecedor',
                        width: 300,
                      },
                      {
                        key: 'manufacturer',
                        dataIndex: ['manufacturer', 'name'],
                        title: 'Fabricante',
                        width: 300,
                      },
                      {
                        key: 'quantity',
                        dataIndex: 'quantity',
                        title: 'Qtde.',
                        render: (value) => formatterNumber(value),
                        width: 100,
                      },
                      {
                        key: 'deliverDays',
                        dataIndex: 'deliverDays',
                        title: 'Entrega(dias)',
                        width: 50,
                      },
                      {
                        key: 'unitCost',
                        dataIndex: 'unitCost',
                        title: 'Valor Unit.',
                        render: (value) => formatterPrice(value),
                        width: 150,
                      },
                      {
                        key: 'discount',
                        dataIndex: 'discount',
                        title: '(%) Desc.',
                        render: (value) => formatterPercent(value),
                        width: 100,
                      },
                      {
                        key: 'totalValue',
                        title: 'Vl. total',
                        render: (_, offer) => {
                          const quantity = offer.quantity ?? 0;
                          const unitCost = offer.unitCost ?? 0;
                          const discount = offer.discount ?? 0;
                          const value =
                            quantity * unitCost * (1 - discount / 100);

                          return formatterPrice(value);
                        },
                        width: 100,
                      },
                      {
                        key: 'freightCost',
                        dataIndex: 'freightCost',
                        title: 'Valor frete',
                        width: 100,
                      },
                    ]}
                    rowSelection={{
                      type: 'radio',
                      selectedRowKeys: selectedSuppliersWinners
                        .filter((item) => item.id === record.id)
                        .map((item) => item.supplier.id),
                      preserveSelectedRowKeys: true,
                      onChange: (_, selectedRows) => {
                        setSelectedSuppliersWinners((prev) => {
                          const filteredPrev = prev.filter(
                            (item) => item.id !== record.id,
                          );

                          const newSelections = selectedRows.map((row) => ({
                            ...row,
                            id: record.id,
                          }));

                          return [...filteredPrev, ...newSelections];
                        });
                      },
                    }}
                    pagination={false}
                    scroll={{ x: true }}
                  />
                ),
              }}
              key={`quotationsOffers-${quotationsOffers?.at(0)?.id}`}
              loading={isFetching}
              rowKey={(row) => row.id}
              dataSource={quotationsOffers}
              columns={columns}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              scroll={{ x: getTableScrollX(columns) }}
            />
          </Col>
        </Row>
      </div>

      <ItemsSelectedListModal
        suppliersSelected={selectedSuppliersWinners}
        open={modalViewItemsSelected}
        onCancel={modalViewItemsSelectedAction.close}
        hasWinnersAlreadySelected={
          quotation?.status === QuotationStatus.WinnersSelected
        }
        quotationId={quotation?.id}
      />

      <Form form={form}>
        <SendRequestPurchaseModal
          open={modalSendRequestPurchase}
          onCancel={modalSendRequestPurchaseAction.close}
          suppliers={selectedSuppliersWinners}
          onCallback={handleCallback}
          setFileList={setFileList}
          loading={quotationSendMutation.isLoading}
        />
      </Form>
    </>
  );
};

export default QuotationPurchaseRequestPage;
